// @ts-nocheck — composed component; types added when touched again later
import React from "react";
import { CHANNEL_LABELS, STAGE_LABELS, stageDisplay } from "../../lib/channels";
// Phase 4 sub-step 10b — explicit primitive import (window publish kept).
import { Button } from "../../legacy/primitives";
const { useState, useEffect, useMemo, useRef } = React;

// Legacy hook destructures — same pattern as src/legacy/app.tsx. By
// module-load time, lib/hooks has populated window.useEscapeClose etc.
// (main.tsx loads it before App). Bare identifiers in the extracted
// function body resolve via this destructure.
const { useEscapeClose, safeDateMs, deepClone } = (typeof window !== "undefined" ? window : {}) as any;

/* composed/NotificationBell.tsx — deal-event notifications (Phase 4
   sub-step 8). Extracted from src/legacy/app.tsx (was inline ~lines
   596-784, 189 LOC).

   Shows a bell in the topbar with a count badge for new closed-won /
   closed-lost / churn events since the last seen-marker (persisted in
   localStorage["forecast_seen_notifs"]). Clicking opens a popover with
   the event list; clicking a row opens the related deal in HubSpot.

   Plays a sound (preset chosen by user, persisted in localStorage) on
   new events. Sound presets live in legacy app.tsx; playNotifSound is
   on window.

   Cross-refs (window-resolved at render time):
     - Button, EmptyState (legacy primitives.tsx)
     - playNotifSound (legacy app.tsx publish block)
     - MONTH_LABELS_*, etc. (lib/model) */
function NotificationBell({ deals, owners, portalId, lang, fmtMoney, notifSound, notifDuration }) {
  const [open, setOpen] = useState(false);
  // Keyboard parity with the backdrop click — Escape closes the bell panel.
  useEscapeClose(open, () => setOpen(false));
  const [seen, setSeen] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("forecast_seen_notifs") || "[]")); }
    catch { return new Set(); }
  });
  const prevEventIdsRef = useRef(null);

  const recentEvents = useMemo(() => {
    const cutoff = Date.now() - (30 * 24 * 3600 * 1000);
    const events = [];
    for (const d of (deals || [])) {
      const meta = STAGE_LABELS[d.stage];
      if (!meta || !(meta.isClosedWon || meta.isClosedLost)) continue;
      const t = new Date(d.closeDate).getTime();
      if (isNaN(t) || t < cutoff) continue;
      events.push({
        id: `${d.id}-${d.stage}`,
        kind: meta.isClosedWon ? "won" : "lost",
        date: d.closeDate,
        ts: t,
        name: d.name,
        company: d.company,
        amount: d.amount,
        ownerName: owners?.[d.ownerId]?.name || "—",
        cell: d.cell,
        dealId: d.id,
      });
    }
    events.sort((a, b) => b.ts - a.ts);
    return events.slice(0, 30);
  }, [deals, owners]);

  const unreadCount = recentEvents.filter(e => !seen.has(e.id)).length;

  // Mark a single notification as seen — clicking the row dismisses just that one.
  const markOneSeen = (id) => {
    if (seen.has(id)) return;
    const next = new Set(seen); next.add(id);
    setSeen(next);
    try { localStorage.setItem("forecast_seen_notifs", JSON.stringify([...next])); } catch {}
  };
  // Optional helper if user wants to clear them all in one go.
  const markAllSeen = () => {
    const next = new Set(seen);
    for (const e of recentEvents) next.add(e.id);
    setSeen(next);
    try { localStorage.setItem("forecast_seen_notifs", JSON.stringify([...next])); } catch {}
  };

  // When a new Closed Won arrives, play the user's selected sound preset (if not muted).
  useEffect(() => {
    const currentIds = new Set(recentEvents.map(e => e.id));
    if (prevEventIdsRef.current !== null && notifSound !== "off") {
      for (const e of recentEvents) {
        if (e.kind === "won" && !prevEventIdsRef.current.has(e.id) && !seen.has(e.id)) {
          playNotifSound(notifSound, notifDuration);
          break;
        }
      }
    }
    prevEventIdsRef.current = currentIds;
  }, [recentEvents, notifSound, notifDuration]);

  const fmtRelativeDay = (iso) => {
    const ms = safeDateMs(iso);
    if (ms == null) return "—";
    const days = Math.floor((Date.now() - ms) / (1000 * 60 * 60 * 24));
    if (days === 0) return lang === "it" ? "oggi" : "today";
    if (days === 1) return lang === "it" ? "ieri" : "yesterday";
    return lang === "it" ? `${days}g fa` : `${days}d ago`;
  };

  return (
    <div className="bell-wrap">
      <Button variant="ghost" size="md" iconOnly
        className={unreadCount > 0 ? "btn-bell--has-unread" : ""}
        onClick={() => setOpen(o => !o)}
        aria-label="Notifications"
        title={lang === "it" ? `Notifiche (${unreadCount} nuove)` : `Notifications (${unreadCount} new)`}>
        🔔
        {unreadCount > 0 && <span className="bell-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>}
      </Button>
      {open && (
        <>
          <div className="bell-backdrop" onClick={() => setOpen(false)} />
          <div className="bell-panel">
            <div className="bell-panel__head">
              <span className="bell-panel__title">{lang === "it" ? "Notifiche" : "Notifications"}</span>
              <span className="bell-panel__sub">
                {recentEvents.length} {lang === "it" ? "eventi · ultimi 30g" : "events · last 30d"}
              </span>
              {unreadCount > 0 && (
                <button className="bell-panel__mark-all" onClick={markAllSeen}
                  title={lang === "it" ? "Segna tutte come lette" : "Mark all as read"}>
                  {lang === "it" ? "✓ tutte" : "✓ all"}
                </button>
              )}
            </div>
            <div className="bell-panel__body">
              {recentEvents.length === 0 && (
                <EmptyState
                  size="compact"
                  icon="🔕"
                  title={lang === "it" ? "Nessun evento recente" : "No recent events"}
                />
              )}
              {recentEvents.map(e => {
                const isNew = !seen.has(e.id);
                const channelMeta = CHANNEL_LABELS[e.cell] || { it: e.cell, en: e.cell, color: "var(--ink-3)" };
                return (
                  <a key={e.id}
                    href={portalId ? `https://app.hubspot.com/contacts/${portalId}/record/0-3/${e.dealId}` : "https://app.hubspot.com/"}
                    target="_blank" rel="noreferrer"
                    onClick={() => markOneSeen(e.id)}
                    className={`bell-item bell-item--${e.kind} ${isNew ? "bell-item--unread" : ""}`}>
                    <span className={`bell-item__icon bell-item__icon--${e.kind}`}>
                      {e.kind === "won" ? "✓" : "×"}
                    </span>
                    <div className="bell-item__body">
                      <div className="bell-item__title">
                        <span className="bell-item__name">{e.name}</span>
                        {isNew && <span className="bell-item__new-dot" title={lang === "it" ? "Nuovo" : "New"} />}
                      </div>
                      <div className="bell-item__meta">
                        <span className="bell-item__channel" style={{color: channelMeta.color}}>{channelMeta[lang]}</span>
                        <span> · {e.ownerName}</span>
                        <span> · {fmtRelativeDay(e.date)}</span>
                      </div>
                    </div>
                    <span className={`bell-item__amount ${e.kind === "lost" ? "bell-item__amount--lost" : ""}`}>
                      {e.kind === "lost" ? "−" : ""}{fmtMoney(e.amount)}
                    </span>
                  </a>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Channel/stage dictionaries moved to src/lib/channels.ts (Phase 4 sub-step
// 8). Imported at the top of this file; same shape, single source of truth
// shared with DealsView.

// Floating popover anchored to a clicked deal card. Renders the full deal details next to
// the card with smart placement (right of the card by default, flips left if no room, then
// flips above/below if vertically constrained). Closes on outside click, Esc, or scroll.

// DealPopover + DealsView extracted to src/components/composed/DealsView.tsx
// (Phase 4 sub-step 5). Imported at top; JSX <DealsView> in Dashboard
// resolves to the composed module.

export default NotificationBell;
